const en = {
  common: {
    loading: "Loading\u2026",
    loadingAlt: "Loading",
    continue: "Continue",
    retry: "Retry",
    goBack: "Go Back",
    logout: "Logout",
    logOut: "Log out",
    email: "Email",
    password: "Password",
  },

  errors: {
    firebase: {
      unexpected: "An unexpected error occurred. Please try again.",
      invalidCredential: "Invalid email or password. Please check your credentials and try again.",
      userNotFound: "No account found with this email. Please sign up first.",
      emailAlreadyInUse: "An account with this email already exists. Please log in instead.",
      weakPassword: "Password is too weak. Please use at least 6 characters.",
      invalidEmail: "Invalid email address. Please enter a valid email.",
      userDisabled: "This account has been disabled. Please contact support.",
      tooManyRequests: "Too many failed attempts. Please try again later or reset your password.",
      networkRequestFailed: "Network error. Please check your internet connection and try again.",
      requiresRecentLogin: "Please log in again to complete this action.",
      popupClosedByUser: "Sign-in cancelled. Please try again.",
      popupBlocked: "Pop-up blocked. Please allow pop-ups for this site and try again.",
      cancelledPopupRequest: "Sign-in cancelled. Please try again.",
      operationNotAllowed: "This sign-in method is not enabled. Please contact support.",
      accountExistsWithDifferentCredential: "An account already exists with the same email but different sign-in method. Please use your original sign-in method.",
      credentialAlreadyInUse: "This credential is already associated with a different account.",
      invalidVerificationCode: "Invalid verification code. Please try again.",
      invalidVerificationId: "Invalid verification ID. Please restart the verification process.",
      missingVerificationCode: "Please enter the verification code.",
      missingVerificationId: "Verification ID is missing. Please restart the verification process.",
      permissionDenied: "You don't have permission to perform this action.",
      unavailable: "Service temporarily unavailable. Please try again in a moment.",
      deadlineExceeded: "Request timed out. Please try again.",
      notFound: "The requested resource was not found.",
      alreadyExists: "This resource already exists.",
      resourceExhausted: "Service limit exceeded. Please try again later.",
      failedPrecondition: "Operation failed. Please ensure all requirements are met.",
      aborted: "Operation was aborted. Please try again.",
      outOfRange: "Invalid range specified.",
      unimplemented: "This feature is not available yet.",
      internal: "Internal server error. Please try again later.",
      dataLoss: "Data loss or corruption detected. Please contact support.",
      unauthenticated: "Please log in to continue.",
      networkError: "Network error. Please check your internet connection.",
      timeoutError: "Request timed out. Please try again.",
      genericError: "An error occurred. Please try again or contact support if the problem persists.",
    },

    payment: {
      default: "Unable to process payment. Please try again.",
      cardDeclined: "Your card was declined. Please check your card details or try a different payment method.",
      cardExpired: "Your card has expired. Please use a different card.",
      cardIncorrect: "The card information is incorrect. Please check and try again.",
      network: "Network error. Please check your connection and try again.",
      cardNotFound: "Card information not found. Please re-enter your card details.",
      authenticationFailed: "Payment authentication failed. Please try again.",
      customerFailed: "Unable to create customer account. Please try again.",
      subscriptionFailed: "Unable to create subscription. Please try again.",
      genericWithSupport: "Unable to process payment. Please try again or contact support.",
    },

    stripe: {
      failedPaymentIntent: "Failed to create payment intent",
      failedCheckoutSession: "Failed to create checkout session",
      subscriptionIdRequired: "Subscription ID is required",
      failedCancelSubscription: "Failed to cancel subscription",
      failedResumeSubscription: "Failed to resume subscription",
      userIdAndPriceRequired: "User ID and new Price ID are required",
      failedUpdateSubscription: "Failed to update subscription",
    },

    stripePricing: {
      fetchPrices: "Failed to fetch prices",
      invalidCoupon: "Invalid coupon code",
      validateCoupon: "Failed to validate coupon code",
      fetchPromoCodes: "Failed to fetch promotion codes",
      getCustomer: "Failed to get or create customer",
      createSubscription: "Failed to create subscription",
      getSubscription: "Failed to get subscription",
    },

    promoCode: {
      default: "Unable to redeem promo code. Please try again.",
      invalidCode: "Invalid promo code",
      expired: "This promo code has expired.",
      activeCode: "You already have an active code. Please wait for it to expire before redeeming another.",
      serviceUnavailable: "Promo code service is temporarily unavailable. Please try again later.",
      unauthenticated: "Please log in to redeem a promo code.",
      permissionDenied: "You do not have permission to redeem this promo code.",
      invalidArgument: "Invalid promo code format. Please check and try again.",
      deadlineExceeded: "Request timed out. Please try again.",
      resourceExhausted: "Service is busy. Please try again in a moment.",
      failedPrecondition: "Cannot redeem this promo code at this time.",
      aborted: "Redemption was cancelled. Please try again.",
      alreadyExists: "This promo code has already been redeemed.",
      unavailable: "Service is currently unavailable. Please try again later.",
      internalError: "An error occurred. Please contact support if this persists.",
      corsError: "Connection error. Please refresh the page and try again.",
      networkError: "Network error. Please check your connection and try again.",
    },
  },

  password: {
    mustContain: "Password must contain {requirements}",
    minLength: "at least 8 characters",
    uppercase: "an uppercase letter",
    lowercase: "a lowercase letter",
    number: "a number",
    specialCharacter: "a special character",
    ensureRequirements: "Please ensure your password meets all requirements",
    requirements: {
      minLength: "At least 8 characters",
      uppercase: "One uppercase letter",
      lowercase: "One lowercase letter",
      number: "One number",
      specialCharacter: "One special character (!@#$%^&*...)",
    },
  },

  login: {
    heading: "Log in to your URUNN account",
    marketingOptIn: "Be the first to hear about new features, events, and expert training tips – delivered straight to your inbox. Unsubscribe anytime.",
    loggingIn: "Logging in...",
    divider: "Or log in with",
    googleButton: "Log in with Google",
    appleButton: "Log in with Apple",
    noAccount: "Don't have an account?",
    signUpLink: "Sign up",
    verifyEmailBeforeLogin: "Please verify your email before logging in. Check your inbox for the verification link.",
    emailNotVerifiedConfirm: "Your email is not verified. Would you like us to resend the verification email?",
    verificationEmailSent: "Verification email sent! Please check your inbox.",
    failedToSendVerification: "Failed to send verification email",
    verifyEmailShort: "Please verify your email before logging in.",
  },

  signup: {
    heading: "Create your URUNN account",
    marketingOptIn: "Be the first to hear about new features, events, and expert training tips – delivered straight to your inbox. Unsubscribe anytime.",
    creatingAccount: "Creating account...",
    divider: "Or sign up with",
    googleButton: "Sign up with Google",
    appleButton: "Sign up with Apple",
    legalPrefix: "By signing up to URUNN, you agree to our",
    termsLink: "Terms and Conditions (EULA)",
    legalAnd: "and",
    privacyLink: "Privacy Policy",
    haveAccount: "Already have an account?",
    loginLink: "Log in",
    verifyHeading: "Verify your email",
    verifyEmailSent: "We've sent a verification email to",
    verifyCheckInbox: "Please check your inbox and click the verification link to activate your account.",
    verifyCheckingStatus: "\u2713 Checking verification status automatically...",
    resending: "Resending...",
    resendVerification: "Resend verification email",
    alreadyVerified: "Already verified?",
    verificationResent: "Verification email resent successfully!",
    failedToResend: "Failed to resend verification email",
    emailAlreadyRegisteredVerify: "This email is already registered. Please verify your email before logging in.",
    emailAlreadyRegisteredPassword: "This email is already registered. Please check your password or use the login page.",
    failedToSendVerification: "Failed to send verification email",
  },

  verifyEmail: {
    heading: "Verify your email",
    sentTo: "We've sent a verification email to",
    checkInbox: "Please check your inbox and click the verification link to activate your account.",
    checkingStatus: "✓ Checking verification status automatically...",
    resend: "Resend verification email",
    resending: "Resending...",
    resent: "Verification email resent successfully!",
    failedToResend: "Failed to resend verification email",
    useDifferentAccount: "Use a different account",
  },

  emailAction: {
    processing: "Processing...",
    pleaseWait: "Please wait while we verify your request",
    invalidRequest: "Invalid request",
    emailVerified: "Your email has been verified",
    verificationExpired: "This verification link has expired or has already been used",
    verifyFailed: "Failed to verify email. Please try again.",
    enterNewPassword: "Enter your new password",
    resetExpired: "This password reset link has expired or has already been used",
    invalidResetLink: "Invalid password reset link",
    passwordsDoNotMatch: "Passwords do not match",
    passwordResetSuccess: "Your password has been reset successfully",
    resetFailed: "Failed to reset password. Please try again.",
    signInMessage: "You can now sign in with your new account",
    emailChanged: "Your email address has been updated",
    emailRecovered: "Your email address has been restored",
    emailChangeFailed: "This link has expired or has already been used",
    resetPasswordTitle: "Reset Your Password",
    enterNewPasswordBelow: "Enter your new password below",
    newPasswordPlaceholder: "New Password",
    confirmPasswordPlaceholder: "Confirm Password",
    resetPasswordButton: "Reset Password",
    errorTitle: "Error",
    goToLogin: "Go to Login",
  },

  billing: {
    stripeNotLoaded: "Stripe has not loaded yet. Please try again.",
    deferredBillingLabel: "Deferred Billing:",
    cardVerificationCancelFailed: "We couldn't verify your card or undo the sign-up. Please contact us from the app before trying again, so you aren't charged twice.",
    deferredBillingMessage: "You will not be charged until your promotional period ends on {date}.",
    emailLabel: "Email",
    cardNumberLabel: "Card number",
    expirationDateLabel: "Expiration date",
    securityCodeLabel: "Security code",
    countryLabel: "Country",
    postalCodeLabel: "Postal code",
    processing: "Processing...",
    startTrial: "Start trial",
    securityNotice: "Payment secured by Stripe. Your card details are encrypted and never stored.",
    termsOfService: "By subscribing, you agree to our Terms of Service",
    autoRenew: "Your subscription will automatically renew unless cancelled",
    cancelAnytime: "You can cancel anytime from your account settings",
    subscriptionStarted: "Subscription started successfully!",
    paymentAuthFailed: "Payment authentication failed. Please try again.",
    updatingPlan: "Updating your plan...",
    subscribeTo: "Subscribe to",
    defaultDiscountName: "Discount",
    discountApplied: "{name} applied: Save {amount}",
    freeTrial: "{duration} free trial",
    monthlyPrice: "{price} / month",
    monthlyPriceAfterTrial: "{price} / month after free trial",
    currentSubscription: "Current Subscription",
    remaining: "Remaining",
    until: "Until",
    chargedOn: "You will be charged on",
    afterTrialEnds: "After trial ends, on {date}",
    totalDueToday: "Total due today",
    dueToday: "Due today",
    planUpdate: "Plan Update",
    planUpdateMessage: "Your subscription will be updated immediately.",
    updating: "Updating...",
    confirmChange: "Confirm Change",
    noPaymentRequired: "No payment details required. Your existing payment method will be used.",
    planUpdatedSuccess: "Plan updated successfully!",
    failedToUpdatePlan: "Failed to update plan. Please try again.",
  },

  offers: {
    loadingOffers: "Loading offers",
    noPlansAvailable: "No subscription plans available",
    failedToLoadPlans: "Failed to load subscription plans. Please try again.",
    title: "Manage Subscription",
    currentPlan: "Current Plan",
    percentOff: "{percent}% OFF",
    enterCouponCode: "Enter coupon code",
    validating: "Validating...",
    apply: "Apply",
    enterPromoCode: "Enter promo code",
    redeeming: "Redeeming...",
    redeem: "Redeem",
    haveCodeRedeem: "Have a promo code? Redeem it",
    continueToPayment: "Continue to payment",
    noPlansMessage: "No subscription plans available at this time.",
    redeemingPromo: "Redeeming promo code...",
    enterPromoCodeError: "Please enter a promo code",
    loginToRedeem: "Please log in to redeem a promo code",
    enterCouponCodeError: "Please enter a coupon code",
    selectPlanFirst: "Please select a plan first",
    unableToValidateCoupon: "Unable to validate coupon for this plan.",
    couponApplied: "Coupon applied! {discount}",
    couponExpired: "This coupon has expired",
    couponLimitReached: "This coupon has reached its usage limit",
    invalidCouponCode: "Invalid coupon code",
    couponNotValidForPlan: "This coupon is not valid for the selected plan",
    unableToValidate: "Unable to validate coupon. Please try again.",
    promoRedeemButNotActive: "Promo code redeemed but subscription not active. Please contact support.",
    unableToRedeemPromo: "Unable to redeem promo code. Please try again.",
    save: "Save {amount}",
    earlyBirdTitle: "Sign up for an annual subscription before your free access ends to lock in a {percent}% discount!",
    earlyBirdFreeUntil: "Keep your free access until {date}. You won't be charged until then.",
    earlyBirdChargedToday: "Your free access has ended. Subscribe today at the early bird price.",
    earlyBirdAlreadySubscribed: "You're already subscribed, so there's nothing more to do.",
    earlyBirdUnavailable: "This early bird offer isn't available on your account.",
    earlyBirdTitleEnded: "Sign up for an annual subscription today to lock in a {percent}% discount!",
    earlyBirdTitleGeneric: "Sign up for an annual subscription to lock in your early bird discount!",
    earlyBirdGoToAccount: "Go to my account",
  },

  subscription: {
    loading: "Loading subscription",
    failedToLoad: "Failed to load subscription details. Please try again.",
    noSubscription: "No subscription found. Redirecting to offers...",
    title: "Your Subscription",
    planLabel: "Plan",
    statusLabel: "Status",
    platformLabel: "Platform",
    priceLabel: "Price",
    startedLabel: "Started",
    renewsLabel: "Renews",
    expiresLabel: "Expires",
    expiryDateLabel: "Expiry date",
    statusFreeTrial: "Free Trial",
    statusActive: "Active",
    statusCancelled: "Cancelled",
    trialBannerTitle: "Free Trial Period",
    trialBannerMessage: "Your trial ends on {date}. You will be charged after this date unless you cancel.",
    cancelledBannerTitle: "Subscription Cancelled",
    cancelledBannerMessage: "Your subscription has been cancelled and will not renew. You still have access until {date}.",
    billingIssue: "There's an issue with your payment method. Please update it to continue your subscription.",
    upgradePlan: "Upgrade Plan",
    changePlan: "Change Plan",
    cancelSubscription: "Cancel Subscription",
    cancelling: "Cancelling...",
    promoNote: "Your promotional access expires on {date}. Upgrade to continue access after that date.",
    stripeNote: "Change to a different plan or cancel anytime. You'll continue to have access until {date}.",
    resumeSubscription: "Resume Subscription",
    resuming: "Resuming...",
    accessEnds: "Your access ends on {date}. Resume to continue your subscription.",
    manageSubscription: "Manage Subscription",
    manageViaStore: "Manage your subscription through {store}.",
    manageViaStoreGeneric: "To cancel or modify your subscription, please manage it through {store}.",
    promotionalAlert: "This is a promotional subscription. Please contact support to manage it.",
    nonStripeAlert: "To cancel your subscription, please contact support or manage it through the store where you purchased it.",
    confirmCancel: "Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.",
    cancelledDefault: "Subscription cancelled successfully. You will have access until the end of your current billing period.",
    failedToCancel: "Failed to cancel subscription. Please try again or contact support.",
    noSubscriptionId: "Unable to find subscription ID. Please contact support.",
    confirmResume: "Are you sure you want to resume your subscription? Your subscription will continue and you will be charged on the next billing date.",
    failedToResume: "Failed to resume subscription. Please try again or contact support.",
    manageSubscriptionAlert: "To cancel or modify your subscription, please contact support or manage it through the store where you purchased it.",
    downloadApp: "Download App",
    scanToDownload: "Scan to download",
    downloadAppCta: "Download the App",
    customPlan: "Custom",
    storeName: {
      appStore: "Apple App Store",
      playStore: "Google Play Store",
      stripe: "Stripe",
      promotional: "Promotional",
    },
  },

  stripe: {
    cancelledAtPeriodEnd: "Subscription will be cancelled on {date}",
    cancelledImmediately: "Subscription cancelled immediately",
    resumedSuccessfully: "Subscription resumed successfully. Your subscription will continue automatically.",
    updatedSuccessfully: "Subscription updated successfully.",
  },

  promoCode: {
    redeemSuccess: "Promo code redeemed successfully!",
  },

  stripePricing: {
    percentOff: "{percent}% OFF",
    amountOff: "{amount} OFF",
    savings: "Save {amount}",
  },
  onboarding: {
    welcomeHeading: "Your AI-powered running coach",
    getStarted: "Get started",
    haveAccount: "I already have an account",
    continue: "Continue",
    skip: "Skip",
    back: "Back",
    createAccount: "Create your account",
    nameFirstPlaceholder: "First name",
    nameLastPlaceholder: "Last name",
    nameRequired: "Please enter your first and last name",
    dateRequired: "Please select a date",
    ageMinimum: "You must be at least 13 years old",
    searchRacePlaceholder: "Search for a race...",
    customRace: "Custom race",
    noRacesFound: "No races found",
    searching: "Searching...",
    skipRace: "I'll skip this for now",
    additionalInfoPlaceholder: "E.g. I'm returning to training after a long-standing calf injury; I have a holiday in 6 weeks and won't be able to run then",
    summaryEdit: "Edit",
  },

  promo: {
    welcomeHeadline: "Welcome to URUNN",
    welcomeSubtitle: "Your AI-powered running coach. Train smarter and run your best.",
    signupSubtitle: "Enter your details to unlock your free access and start training.",
    loginSubtitle: "Welcome back. Log in to continue your training.",
    getStarted: "Get started",
    alreadyHaveAccount: "Already have an account?",
    logIn: "Log in",
    successLead: "Your next best run starts here.",
    redeemHeadline: "Redeem your code",
    redeemSubtitle: "Enter your code below to unlock your free access and start training.",
    successTitle: "SUCCESS!",
    redeemSuccessSub: "Your subscription is active. We've emailed you a magic link to download the app and get started.",
    viewSubscription: "View my subscription",
    downloadAppCta: "Click to Download the App",
  },
} as const;

export default en;
